
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useDrawings } from "@/hooks/use-drawings";
import { useImageLoader } from "@/hooks/use-image-loader";
import { useDrawingLoader } from "@/hooks/use-drawing-loader";
import { LoadingDrawingsGrid } from "./LoadingDrawingsGrid";
import { EmptyDrawingState } from "./EmptyDrawingState";
import { DrawingsGrid } from "./DrawingsGrid";
import { DrawingsList } from "./DrawingsList";
import { LastUsedDrawings } from "./LastUsedDrawings";
import { DrawingHistoryHeader } from "./DrawingHistoryHeader";
import { LoadingIndicator } from "../LoadingIndicator";
import { Tables } from "@/integrations/supabase/types";

type Drawing = Tables<"whiteboard_history"> & {
  drawing_analyses?: {
    id: string;
    procedure_type: string;
    analysis_text: string;
    created_at: string;
    drawing_id: string;
    key_points: Json | null;
    status: "pending" | "completed" | "failed";
    updated_at: string;
    user_id: string;
  }[];
};

type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

interface DrawingHistorySidebarProps {
  onLoadDrawing: (data: string, drawingId: string, analysisData?: any) => void;
  onRefreshNeeded?: (refreshFn: () => void) => void;
}

export function DrawingHistorySidebar({ onLoadDrawing, onRefreshNeeded }: DrawingHistorySidebarProps) {
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [lastUsedDrawings, setLastUsedDrawings] = useState<Drawing[]>([]);
  const [isLoadingLastUsed, setIsLoadingLastUsed] = useState(true);
  const ITEMS_PER_PAGE = 6;

  const {
    drawings,
    isLoading,
    hasMore,
    searchQuery,
    setSearchQuery,
    selectedTags,
    setSelectedTags,
    availableTags,
    loadMoreDrawings,
    fetchDrawings
  } = useDrawings(ITEMS_PER_PAGE);

  const {
    loadedImages,
    staggerLoadImages
  } = useImageLoader();

  const {
    loadingDrawingId,
    handleLoad
  } = useDrawingLoader(onLoadDrawing);

  // Determine if we should show the Last Saved section
  const shouldShowLastSaved = !searchQuery && selectedTags.length === 0;

  // Effect for loading images
  useEffect(() => {
    if (drawings.length > 0) {
      console.log('Starting staggered image loading for drawings:', drawings.length);
      staggerLoadImages(drawings);
    }
  }, [drawings, staggerLoadImages]);

  // Fetch last used drawings
  useEffect(() => {
    const fetchLastUsed = async () => {
      setIsLoadingLastUsed(true);
      try {
        const { data, error } = await supabase
          .from("whiteboard_history")
          .select(`
            id,
            title,
            description,
            preview_image,
            tags,
            created_at,
            updated_at,
            drawing_data,
            featured,
            is_public,
            user_id,
            view_count,
            last_modified_at,
            last_modified_by,
            version,
            drawing_analyses (*)
          `)
          .order('updated_at', { ascending: false })
          .limit(4);

        if (error) throw error;
        
        if (data) {
          console.log('Fetched last used drawings:', data.length);
          setLastUsedDrawings(data as Drawing[]);
          staggerLoadImages(data as Drawing[]);
        }
      } catch (error) {
        console.error('Error fetching last used drawings:', error);
      } finally {
        setIsLoadingLastUsed(false);
      }
    };

    fetchLastUsed();
  }, [staggerLoadImages]);

  // Determine overall loading state
  const isLoadingAny = isLoading || isLoadingLastUsed || (loadedImages.size === 0 && (drawings.length > 0 || lastUsedDrawings.length > 0));

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("whiteboard_history")
        .delete()
        .eq("id", id);

      if (error) throw error;

      fetchDrawings();
      const updatedLastUsed = lastUsedDrawings.filter(d => d.id !== id);
      setLastUsedDrawings(updatedLastUsed);
      
      toast({
        title: "Drawing deleted",
        description: "The drawing has been removed from your history.",
      });
    } catch (error) {
      console.error("Error deleting drawing:", error);
      toast({
        variant: "destructive",
        title: "Error deleting drawing",
        description: "There was a problem deleting your drawing.",
      });
    }
  };

  const handleToggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden relative">
      <DrawingHistoryHeader
        viewMode={viewMode}
        setViewMode={setViewMode}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedTags={selectedTags}
        availableTags={availableTags}
        onToggleTag={handleToggleTag}
      />

      <div className="flex-1 overflow-y-auto p-4">
        {/* Global loading indicator */}
        <LoadingIndicator 
          message="Loading drawings..." 
          isSaving={isLoadingAny}
        />

        {!isLoading && shouldShowLastSaved && lastUsedDrawings.length > 0 && (
          <LastUsedDrawings
            drawings={lastUsedDrawings}
            loadedImages={loadedImages}
            loadingDrawingId={loadingDrawingId}
            onLoad={handleLoad}
            onDelete={handleDelete}
          />
        )}
        
        {isLoading ? (
          <LoadingDrawingsGrid count={ITEMS_PER_PAGE} />
        ) : drawings.length === 0 ? (
          <EmptyDrawingState />
        ) : viewMode === "grid" ? (
          <DrawingsGrid
            drawings={drawings}
            loadedImages={loadedImages}
            loadingDrawingId={loadingDrawingId}
            hasMore={hasMore}
            onLoad={handleLoad}
            onDelete={handleDelete}
            onLoadMore={loadMoreDrawings}
          />
        ) : (
          <DrawingsList
            drawings={drawings}
            loadedImages={loadedImages}
            loadingDrawingId={loadingDrawingId}
            hasMore={hasMore}
            onLoad={handleLoad}
            onDelete={handleDelete}
            onLoadMore={loadMoreDrawings}
          />
        )}
      </div>
    </div>
  );
}
